/**
 * POS Staff 50% Discount Component - SIMPLIFIED
 * 
 * Flow:
 * 1. Cart already has items (menu shown ONCE at POS)
 * 2. Cashier taps "Staff 50%" 
 * 3. This modal shows ACTIVE staff list
 * 4. Cashier selects staff
 * 5. Shows 50% discount applied + payment options (including Add to Tab)
 * 6. Done
 */
import { useState, useEffect } from 'react';
import { 
  User, Search, Loader2, X, Percent, Wallet, CreditCard,
  DollarSign, QrCode, Receipt
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import api from '../../utils/apiClient';

const STAFF_DISCOUNT_PERCENT = 50;

export default function POSStaffDiscount({ 
  cartItems,        // Items already in cart
  cartSubtotal,     // Subtotal before discount
  staffId,          // Current cashier's staff ID for auth
  onClose, 
  onComplete        // Called with { staffMember, paymentMethod, discountedTotal }
}) {
  const [activeStaff, setActiveStaff] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('add_to_tab');
  const [processing, setProcessing] = useState(false);

  // Calculate discount
  const discountAmount = cartSubtotal * (STAFF_DISCOUNT_PERCENT / 100);
  const discountedTotal = cartSubtotal - discountAmount;

  // Fetch ALL active staff on mount
  useEffect(() => {
    fetchActiveStaff();
  }, []);

  const fetchActiveStaff = async () => {
    setLoadingStaff(true);
    try {
      const res = await api.get('/staff/active');
      setActiveStaff(res.data.staff || []);
    } catch (err) {
      console.error('Failed to fetch staff:', err);
      // Fallback to search endpoint
      try {
        const authParam = staffId ? `&staff_id=${staffId}` : '';
        const res = await api.get(`/pos/staff-lookup?query=a${authParam}`);
        setActiveStaff(res.data.staff || []);
      } catch (e) {
        toast.error('Failed to load staff list');
      }
    } finally {
      setLoadingStaff(false);
    }
  };

  // Filter staff by search
  const filteredStaff = searchQuery.trim()
    ? activeStaff.filter(s => 
        s.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        s.role?.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : activeStaff;

  // Handle complete
  const handleComplete = async () => {
    if (!selectedStaff) {
      toast.error('Please select a staff member');
      return;
    }

    setProcessing(true);
    try {
      // If adding to tab, call the staff tab endpoint
      if (paymentMethod === 'add_to_tab') {
        const res = await api.post('/pos/staff-tab/add', {
          staff_member_id: selectedStaff.id,
          staff_member_name: selectedStaff.name,
          items: cartItems.map(item => ({
            name: item.name,
            quantity: item.quantity,
            original_price: item.price,
            discounted_price: item.price * 0.5
          })),
          original_total: cartSubtotal,
          discount_percent: STAFF_DISCOUNT_PERCENT,
          discount_amount: discountAmount,
          final_total: discountedTotal,
          cashier_id: staffId
        });

        if (res.data.success) {
          toast.success(`Added to ${selectedStaff.name}'s tab: $${discountedTotal.toFixed(2)}`);
          onComplete({
            staffMember: selectedStaff,
            paymentMethod: 'add_to_tab',
            discountedTotal,
            tabBalance: res.data.new_balance
          });
        }
      } else {
        // Regular payment - just apply discount and proceed
        onComplete({
          staffMember: selectedStaff,
          paymentMethod,
          discountedTotal,
          discountAmount,
          discountLabel: `Staff 50% (${selectedStaff.name})`
        });
      }
    } catch (err) {
      console.error('Staff discount error:', err);
      toast.error(err.response?.data?.detail || 'Failed to process staff discount');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={onClose}>
      <div 
        className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-lg mx-4 shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
        data-testid="staff-discount-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Percent className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white">Staff 50% Discount</h2>
              <p className="text-purple-100 text-sm">Select staff member</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Order Summary */}
          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
            <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>Original Total:</span>
              <span>${cartSubtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm text-green-600 dark:text-green-400">
              <span>50% Discount:</span>
              <span>-${discountAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-lg font-bold text-purple-700 dark:text-purple-300 mt-1 pt-1 border-t border-purple-200 dark:border-purple-800">
              <span>Staff Price:</span>
              <span>${discountedTotal.toFixed(2)}</span>
            </div>
          </div>

          {/* Staff Selection */}
          {!selectedStaff ? (
            <>
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search staff by name..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="pl-10"
                  autoFocus
                />
              </div>

              {/* Staff List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {loadingStaff ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
                    <span className="ml-2 text-gray-500">Loading staff...</span>
                  </div>
                ) : filteredStaff.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    {searchQuery ? 'No staff found' : 'No active staff'}
                  </div>
                ) : (
                  filteredStaff.map(staff => (
                    <button
                      key={staff.id}
                      onClick={() => setSelectedStaff(staff)}
                      className="w-full flex items-center gap-3 p-3 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-purple-500 dark:hover:border-purple-400 transition-all text-left"
                      data-testid={`staff-option-${staff.id}`}
                    >
                      <div className="w-10 h-10 bg-purple-100 dark:bg-purple-900 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-white">{staff.name}</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{staff.role}</p>
                      </div>
                      {staff.tab_balance > 0 && (
                        <span className="text-xs bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 px-2 py-1 rounded">
                          Tab: ${staff.tab_balance.toFixed(2)}
                        </span>
                      )}
                    </button>
                  ))
                )}
              </div>
            </>
          ) : (
            <>
              {/* Selected Staff */}
              <div className="flex items-center gap-3 p-3 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                  <p className="font-bold text-gray-900 dark:text-white">{selectedStaff.name}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 capitalize">{selectedStaff.role}</p>
                </div>
                <button 
                  onClick={() => setSelectedStaff(null)}
                  className="text-sm text-purple-600 hover:text-purple-800 dark:text-purple-400"
                >
                  Change
                </button>
              </div>

              {/* Payment Options */}
              <div>
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Payment Method</p>
                <div className="grid grid-cols-2 gap-2">
                  {/* Add to Tab - Primary Option */}
                  <button
                    onClick={() => setPaymentMethod('add_to_tab')}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'add_to_tab'
                        ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-purple-300'
                    }`}
                    data-testid="payment-add-to-tab"
                  >
                    <Receipt className="w-5 h-5 mb-1 text-purple-600" />
                    <span className="text-sm font-medium">Add to Tab</span>
                  </button>

                  {/* Cash */}
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'cash'
                        ? 'border-green-500 bg-green-50 dark:bg-green-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-green-300'
                    }`}
                    data-testid="payment-cash"
                  >
                    <DollarSign className="w-5 h-5 mb-1 text-green-600" />
                    <span className="text-sm font-medium">Cash</span>
                  </button>

                  {/* EFTPOS */}
                  <button
                    onClick={() => setPaymentMethod('eftpos')}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'eftpos'
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-blue-300'
                    }`}
                    data-testid="payment-eftpos"
                  >
                    <CreditCard className="w-5 h-5 mb-1 text-blue-600" />
                    <span className="text-sm font-medium">EFTPOS</span>
                  </button>

                  {/* M-PAiSA */}
                  <button
                    onClick={() => setPaymentMethod('mpaisa')}
                    className={`flex flex-col items-center p-3 rounded-lg border-2 transition-all ${
                      paymentMethod === 'mpaisa'
                        ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/30'
                        : 'border-gray-200 dark:border-gray-700 hover:border-orange-300'
                    }`}
                    data-testid="payment-mpaisa"
                  >
                    <QrCode className="w-5 h-5 mb-1 text-orange-600" />
                    <span className="text-sm font-medium">M-PAiSA</span>
                  </button>
                </div>
              </div>

              {/* Add to Tab Info */}
              {paymentMethod === 'add_to_tab' && (
                <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-3 text-sm">
                  <p className="text-amber-800 dark:text-amber-200">
                    <strong>${discountedTotal.toFixed(2)}</strong> will be added to {selectedStaff.name}&apos;s tab.
                    {selectedStaff.tab_balance > 0 && (
                      <span> Current tab: ${selectedStaff.tab_balance.toFixed(2)}</span>
                    )}
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
          {selectedStaff ? (
            <Button
              onClick={handleComplete}
              disabled={processing}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white py-3"
              data-testid="confirm-staff-discount"
            >
              {processing ? (
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
              ) : null}
              {paymentMethod === 'add_to_tab' 
                ? `Add $${discountedTotal.toFixed(2)} to Tab`
                : `Pay $${discountedTotal.toFixed(2)} with ${paymentMethod.toUpperCase()}`
              }
            </Button>
          ) : (
            <Button
              onClick={onClose}
              variant="outline"
              className="w-full"
            >
              Cancel
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
